import React, { useEffect, useMemo, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showConfirmation, showError, showSuccess } from '../../services/alert';

const sexoOptions = [
  { value: 1, label: 'Macho' },
  { value: 2, label: 'Femea' }
];

const categoriaOptions = [
  { value: 1, label: 'Bezerro' },
  { value: 2, label: 'Bezerra' },
  { value: 3, label: 'Novilha' },
  { value: 4, label: 'Garrote' },
  { value: 5, label: 'Touro' },
  { value: 6, label: 'Vaca' },
  { value: 7, label: 'Matriz' },
  { value: 8, label: 'Reprodutor' },
  { value: 9, label: 'Engorda' },
  { value: 10, label: 'Leiteiro' }
];

const statusLabels = {
  1: 'Ativo',
  2: 'Vendido',
  3: 'Morto',
  4: 'Abatido',
  5: 'Transferido'
};

const initialLote = {
  propriedadeId: '',
  nome: '',
  descricao: ''
};

const initialAnimal = {
  propriedadeId: '',
  lotePecuariaId: '',
  maeId: '',
  paiId: '',
  brinco: '',
  codigoInterno: '',
  nome: '',
  sexo: 1,
  raca: '',
  categoria: 1,
  dataNascimento: '',
  pesoInicial: '',
  valorCompra: '',
  observacao: ''
};

const initialCompra = {
  propriedadeId: '',
  lotePecuariaId: '',
  fornecedorId: '',
  dataCompra: new Date().toISOString().slice(0, 10),
  dataVencimento: new Date().toISOString().slice(0, 10),
  formaPagamento: 0,
  documentoReferencia: '',
  brinco: '',
  codigoInterno: '',
  nome: '',
  sexo: 1,
  raca: '',
  categoria: 1,
  dataNascimento: '',
  pesoEntrada: '',
  valor: '',
  observacao: ''
};

const initialVenda = {
  propriedadeId: '',
  clienteId: '',
  dataVenda: new Date().toISOString().slice(0, 10),
  dataRecebimento: new Date().toISOString().slice(0, 10),
  precoKg: '',
  formaPagamento: 0,
  emitirNotaFiscal: true,
  observacao: ''
};

const initialRotinaAnimal = {
  animalId: '',
  loteDestinoId: '',
  dataEvento: new Date().toISOString().slice(0, 10),
  peso: '',
  valor: '',
  documentoReferencia: '',
  observacao: '',
  dataPesagem: new Date().toISOString().slice(0, 10),
  pesoPesagem: '',
  medicamento: '',
  dataAplicacao: new Date().toISOString().slice(0, 10),
  responsavel: '',
  proximaDose: '',
  dataLeite: new Date().toISOString().slice(0, 10),
  litros: '',
  turno: 1,
  insumoId: '',
  loteNutricaoId: '',
  categoriaNutricao: 1,
  dataFornecimento: new Date().toISOString().slice(0, 10),
  quantidadeNutricao: '',
  unidadeNutricao: 'KG',
  custoNutricao: '',
  matrizId: '',
  reprodutorId: '',
  dataInseminacao: '',
  dataCobertura: '',
  diagnostico: 1,
  dataDiagnostico: '',
  dataParto: '',
  maeId: '',
  paiIdNascimento: '',
  brincoNascimento: '',
  codigoNascimento: '',
  nomeNascimento: '',
  sexoNascimento: 1,
  racaNascimento: '',
  categoriaNascimento: 1,
  dataNascimentoNovo: new Date().toISOString().slice(0, 10),
  pesoNascimento: '',
  propriedadeNascimentoId: '',
  loteNascimentoId: ''
};

const turnoLeiteOptions = [
  { value: 1, label: 'Manha' },
  { value: 2, label: 'Tarde' },
  { value: 3, label: 'Noite' }
];

const diagnosticoOptions = [
  { value: 1, label: 'Aguardando' },
  { value: 2, label: 'Prenhe' },
  { value: 3, label: 'Vazia' },
  { value: 4, label: 'Pariu' },
  { value: 5, label: 'Perda' }
];

const nutricaoOptions = [
  { value: 1, label: 'Racao' },
  { value: 2, label: 'Silagem' },
  { value: 3, label: 'Milho' },
  { value: 4, label: 'Sal mineral' },
  { value: 5, label: 'Suplemento' }
];

const pecuariaTabs = [
  { key: 'visao', label: 'Visão geral', icon: 'bi-speedometer2' },
  { key: 'cadastros', label: 'Cadastros', icon: 'bi-plus-square' },
  { key: 'movimentacoes', label: 'Compra e venda', icon: 'bi-arrow-left-right' },
  { key: 'rotinas', label: 'Rotinas', icon: 'bi-calendar-check' },
  { key: 'historico', label: 'Histórico por Animal', icon: 'bi-clock-history' },
  { key: 'consultas', label: 'Consultas', icon: 'bi-table' }
];

function numberValue(value) {
  return value === '' || value === null || value === undefined ? null : Number(value);
}

function getApiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    return data;
  }

  return data?.message || data?.Message || fallback;
}

function PecuariaDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [propriedades, setPropriedades] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [compras, setCompras] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loteForm, setLoteForm] = useState(initialLote);
  const [animalForm, setAnimalForm] = useState(initialAnimal);
  const [compraForm, setCompraForm] = useState(initialCompra);
  const [vendaForm, setVendaForm] = useState(initialVenda);
  const [rotinaForm, setRotinaForm] = useState(initialRotinaAnimal);
  const [animaisVenda, setAnimaisVenda] = useState([]);
  const [activeTab, setActiveTab] = useState('visao');
  const [historicoAnimal, setHistoricoAnimal] = useState([]);
  const [filtroHistorico, setFiltroHistorico] = useState({ tipo: '', inicio: '', fim: '' });

  const carregarHistoricoAnimal = async (animalId) => {
    if (!animalId) { setHistoricoAnimal([]); return; }
    try {
      const response = await api.get(`/pecuaria/animais/${animalId}/historico`);
      setHistoricoAnimal(Array.isArray(response.data) ? response.data : []);
    } catch {
      setHistoricoAnimal([]);
    }
  };

  useEffect(() => { carregarHistoricoAnimal(rotinaForm.animalId); }, [rotinaForm.animalId]);

  const historicoFiltrado = useMemo(() => historicoAnimal.filter((item) => {
    const data = String(item.data || '').slice(0, 10);
    return (!filtroHistorico.tipo || item.tipo === filtroHistorico.tipo)
      && (!filtroHistorico.inicio || data >= filtroHistorico.inicio)
      && (!filtroHistorico.fim || data <= filtroHistorico.fim);
  }), [historicoAnimal, filtroHistorico]);

  const propriedadeSelecionada = useMemo(
    () => propriedades.find((item) => String(item.id) === String(animalForm.propriedadeId)),
    [propriedades, animalForm.propriedadeId]
  );

  const lotesDisponiveis = useMemo(
    () => lotes.filter((lote) => !animalForm.propriedadeId || String(lote.propriedadeId) === String(animalForm.propriedadeId)),
    [lotes, animalForm.propriedadeId]
  );

  const lotesCompraDisponiveis = useMemo(
    () => lotes.filter((lote) => !compraForm.propriedadeId || String(lote.propriedadeId) === String(compraForm.propriedadeId)),
    [lotes, compraForm.propriedadeId]
  );

  const animaisAtivosVenda = useMemo(
    () => animais.filter((animal) => animal.status === 1 && (!vendaForm.propriedadeId || String(animal.propriedadeId) === String(vendaForm.propriedadeId))),
    [animais, vendaForm.propriedadeId]
  );

  const animalSelecionado = useMemo(
    () => animais.find((animal) => String(animal.id) === String(rotinaForm.animalId)),
    [animais, rotinaForm.animalId]
  );

  const lotesRotinaDisponiveis = useMemo(
    () => lotes.filter((lote) => !animalSelecionado || String(lote.propriedadeId) === String(animalSelecionado.propriedadeId)),
    [lotes, animalSelecionado]
  );

  const lotesNascimentoDisponiveis = useMemo(
    () => lotes.filter((lote) => !rotinaForm.propriedadeNascimentoId || String(lote.propriedadeId) === String(rotinaForm.propriedadeNascimentoId)),
    [lotes, rotinaForm.propriedadeNascimentoId]
  );

  const resumoVenda = useMemo(() => {
    const selecionados = animaisAtivosVenda.filter((animal) => animaisVenda.includes(animal.id));
    const peso = selecionados.reduce((total, animal) => total + Number(animal.pesoAtual ?? animal.pesoInicial ?? 0), 0);
    const valor = peso * Number(vendaForm.precoKg || 0);
    return { quantidade: selecionados.length, peso, valor };
  }, [animaisAtivosVenda, animaisVenda, vendaForm.precoKg]);

  const carregar = async () => {
    setLoading(true);
    try {
      const [dashRes, propriedadesRes, lotesRes, animaisRes, comprasRes, vendasRes, fornecedoresRes, clientesRes, insumosRes] = await Promise.all([
        api.get('/pecuaria/dashboard'),
        api.get('/propriedades'),
        api.get('/pecuaria/lotes'),
        api.get('/pecuaria/animais'),
        api.get('/pecuaria/compras'),
        api.get('/pecuaria/vendas'),
        api.get('/fornecedor'),
        api.get('/cliente'),
        api.get('/insumos')
      ]);

      setDashboard(dashRes.data);
      setPropriedades(Array.isArray(propriedadesRes.data) ? propriedadesRes.data : []);
      setLotes(Array.isArray(lotesRes.data) ? lotesRes.data : []);
      setAnimais(Array.isArray(animaisRes.data) ? animaisRes.data : []);
      setCompras(Array.isArray(comprasRes.data) ? comprasRes.data : []);
      setVendas(Array.isArray(vendasRes.data) ? vendasRes.data : []);
      setFornecedores(Array.isArray(fornecedoresRes.data) ? fornecedoresRes.data : []);
      setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      setInsumos(Array.isArray(insumosRes.data) ? insumosRes.data : []);
    } catch (error) {
      console.error('Erro ao carregar pecuária', error);
      showError(getApiErrorMessage(error, 'Não foi possível carregar a pecuária.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvarLote = async (event) => {
    event.preventDefault();
    try {
      await api.post('/pecuaria/lotes', {
        propriedadeId: Number(loteForm.propriedadeId),
        nome: loteForm.nome,
        descricao: loteForm.descricao || null
      });
      setLoteForm(initialLote);
      showSuccess('Lote cadastrado.');
      carregar();
    } catch (error) {
      console.error('Erro ao salvar lote', error);
      showError(getApiErrorMessage(error, 'Não foi possível salvar o lote.'));
    }
  };

  const salvarAnimal = async (event) => {
    event.preventDefault();
    try {
      await api.post('/pecuaria/animais', {
        propriedadeId: Number(animalForm.propriedadeId),
        lotePecuariaId: Number(animalForm.lotePecuariaId),
        maeId: numberValue(animalForm.maeId),
        paiId: numberValue(animalForm.paiId),
        brinco: animalForm.brinco,
        codigoInterno: animalForm.codigoInterno,
        nome: animalForm.nome || null,
        sexo: Number(animalForm.sexo),
        raca: animalForm.raca,
        categoria: Number(animalForm.categoria),
        dataNascimento: animalForm.dataNascimento || null,
        pesoInicial: Number(animalForm.pesoInicial || 0),
        valorCompra: numberValue(animalForm.valorCompra),
        observacao: animalForm.observacao || null
      });
      setAnimalForm(initialAnimal);
      showSuccess('Animal cadastrado com histórico.');
      carregar();
    } catch (error) {
      console.error('Erro ao salvar animal', error);
      showError(getApiErrorMessage(error, 'Não foi possível salvar o animal.'));
    }
  };

  const registrarCompra = async (event) => {
    event.preventDefault();
    try {
      await api.post('/pecuaria/compras', {
        propriedadeId: Number(compraForm.propriedadeId),
        lotePecuariaId: Number(compraForm.lotePecuariaId),
        fornecedorId: numberValue(compraForm.fornecedorId),
        dataCompra: compraForm.dataCompra,
        dataVencimento: compraForm.dataVencimento,
        formaPagamento: Number(compraForm.formaPagamento),
        documentoReferencia: compraForm.documentoReferencia || null,
        observacao: compraForm.observacao || null,
        itens: [{
          brinco: compraForm.brinco,
          codigoInterno: compraForm.codigoInterno,
          nome: compraForm.nome || null,
          sexo: Number(compraForm.sexo),
          raca: compraForm.raca,
          categoria: Number(compraForm.categoria),
          dataNascimento: compraForm.dataNascimento || null,
          pesoEntrada: Number(compraForm.pesoEntrada || 0),
          valor: Number(compraForm.valor || 0),
          observacao: compraForm.observacao || null
        }]
      });
      setCompraForm(initialCompra);
      showSuccess('Compra registrada com conta a pagar e histórico.');
      carregar();
    } catch (error) {
      console.error('Erro ao registrar compra', error);
      showError(getApiErrorMessage(error, 'Não foi possível registrar a compra.'));
    }
  };

  const registrarVenda = async (event) => {
    event.preventDefault();
    try {
      await api.post('/pecuaria/vendas', {
        propriedadeId: Number(vendaForm.propriedadeId),
        clienteId: Number(vendaForm.clienteId),
        dataVenda: vendaForm.dataVenda,
        dataRecebimento: vendaForm.dataRecebimento,
        precoKg: Number(vendaForm.precoKg || 0),
        formaPagamento: Number(vendaForm.formaPagamento),
        emitirNotaFiscal: Boolean(vendaForm.emitirNotaFiscal),
        observacao: vendaForm.observacao || null,
        animalIds: animaisVenda
      });
      setVendaForm(initialVenda);
      setAnimaisVenda([]);
      showSuccess('Venda registrada com baixa do animal e conta a receber.');
      carregar();
    } catch (error) {
      console.error('Erro ao registrar venda', error);
      showError(getApiErrorMessage(error, 'Não foi possível registrar a venda.'));
    }
  };

  const emitirNotaVenda = async (vendaId) => {
    try {
      await api.post(`/pecuaria/vendas/${vendaId}/emitir-nfe`, {
        ambiente: 'Producao',
        descricao: 'Venda de animais vivos',
        ncm: '01022990',
        cfop: '5102',
        unidade: 'KG',
        valorFrete: 0,
        valorDesconto: 0
      });
      showSuccess('NF-e enviada para a ACBr API.');
      carregar();
    } catch (error) {
      console.error('Erro ao emitir NF-e da venda pecuária', error);
      showError(getApiErrorMessage(error, 'Não foi possível emitir a NF-e da venda.'));
    }
  };

  const baixarAnimal = async (animalId, tipo) => {
    const confirmado = await showConfirmation({
      title: tipo === 'morte' ? 'Registrar morte?' : 'Registrar abate?',
      text: 'O animal deixará de aparecer como ativo e o histórico será registrado.',
      confirmText: 'Confirmar',
      cancelText: 'Cancelar'
    });
    if (!confirmado) return;

    try {
      await api.post(`/pecuaria/animais/${animalId}/${tipo}`, {
        dataEvento: new Date().toISOString(),
        peso: null,
        valor: null,
        documentoReferencia: null,
        observacao: tipo === 'morte' ? 'Baixa por morte' : 'Baixa por abate'
      });
      showSuccess('Baixa registrada no histórico.');
      carregar();
    } catch (error) {
      console.error('Erro ao baixar animal', error);
      showError(getApiErrorMessage(error, 'Não foi possível baixar o animal.'));
    }
  };

  const setRotina = (field, value) => {
    setRotinaForm((atual) => ({ ...atual, [field]: value }));
  };

  const postRotina = async (request, sucesso) => {
    try {
      await request();
      showSuccess(sucesso);
      await carregar();
      await carregarHistoricoAnimal(rotinaForm.animalId);
    } catch (error) {
      console.error('Erro na rotina pecuária', error);
      showError(getApiErrorMessage(error, 'Não foi possível registrar a rotina.'));
    }
  };

  const registrarTransferencia = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post(`/pecuaria/animais/${rotinaForm.animalId}/transferir`, {
        loteDestinoId: Number(rotinaForm.loteDestinoId),
        dataEvento: rotinaForm.dataEvento,
        observacao: rotinaForm.observacao || null
      }),
      'Transferência registrada no histórico.'
    );
  };

  const registrarBaixaManual = async (event, tipo) => {
    event.preventDefault();
    await postRotina(
      () => api.post(`/pecuaria/animais/${rotinaForm.animalId}/${tipo}`, {
        dataEvento: rotinaForm.dataEvento,
        peso: numberValue(rotinaForm.peso),
        valor: numberValue(rotinaForm.valor),
        documentoReferencia: rotinaForm.documentoReferencia || null,
        observacao: rotinaForm.observacao || null
      }),
      tipo === 'morte' ? 'Morte registrada no histórico.' : 'Abate registrado no histórico.'
    );
  };

  const registrarPesagem = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post(`/pecuaria/animais/${rotinaForm.animalId}/pesagens`, {
        dataPesagem: rotinaForm.dataPesagem,
        peso: Number(rotinaForm.pesoPesagem || 0),
        observacao: rotinaForm.observacao || null
      }),
      'Pesagem registrada.'
    );
  };

  const registrarSanidade = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post(`/pecuaria/animais/${rotinaForm.animalId}/sanidade`, {
        medicamento: rotinaForm.medicamento,
        dataAplicacao: rotinaForm.dataAplicacao,
        responsavel: rotinaForm.responsavel,
        proximaDose: rotinaForm.proximaDose || null,
        observacao: rotinaForm.observacao || null
      }),
      'Sanidade registrada.'
    );
  };

  const registrarLeite = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post(`/pecuaria/animais/${rotinaForm.animalId}/leite`, {
        dataProducao: rotinaForm.dataLeite,
        litros: Number(rotinaForm.litros || 0),
        turno: Number(rotinaForm.turno),
        observacao: rotinaForm.observacao || null
      }),
      'Produção de leite registrada.'
    );
  };

  const registrarNutricao = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post('/pecuaria/nutricao', {
        animalPecuariaId: numberValue(rotinaForm.animalId),
        lotePecuariaId: numberValue(rotinaForm.loteNutricaoId),
        insumoId: numberValue(rotinaForm.insumoId),
        categoria: Number(rotinaForm.categoriaNutricao),
        dataFornecimento: rotinaForm.dataFornecimento,
        quantidade: Number(rotinaForm.quantidadeNutricao || 0),
        unidade: rotinaForm.unidadeNutricao,
        custoTotal: Number(rotinaForm.custoNutricao || 0),
        observacao: rotinaForm.observacao || null
      }),
      'Nutrição registrada no custo pecuário.'
    );
  };

  const registrarReproducao = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post('/pecuaria/reproducao', {
        matrizId: Number(rotinaForm.matrizId || rotinaForm.animalId),
        reprodutorId: numberValue(rotinaForm.reprodutorId),
        dataInseminacao: rotinaForm.dataInseminacao || null,
        dataCobertura: rotinaForm.dataCobertura || null,
        diagnostico: Number(rotinaForm.diagnostico),
        dataDiagnostico: rotinaForm.dataDiagnostico || null,
        dataParto: rotinaForm.dataParto || null,
        observacao: rotinaForm.observacao || null
      }),
      'Reprodução registrada.'
    );
  };

  const registrarNascimento = async (event) => {
    event.preventDefault();
    await postRotina(
      () => api.post('/pecuaria/nascimentos', {
        propriedadeId: Number(rotinaForm.propriedadeNascimentoId || animalSelecionado?.propriedadeId || 0),
        lotePecuariaId: Number(rotinaForm.loteNascimentoId),
        maeId: Number(rotinaForm.maeId || rotinaForm.animalId),
        paiId: numberValue(rotinaForm.paiIdNascimento),
        brinco: rotinaForm.brincoNascimento,
        codigoInterno: rotinaForm.codigoNascimento,
        nome: rotinaForm.nomeNascimento || null,
        sexo: Number(rotinaForm.sexoNascimento),
        raca: rotinaForm.racaNascimento,
        categoria: Number(rotinaForm.categoriaNascimento),
        dataNascimento: rotinaForm.dataNascimentoNovo,
        pesoNascimento: Number(rotinaForm.pesoNascimento || 0),
        observacao: rotinaForm.observacao || null
      }),
      'Nascimento registrado e novo animal criado.'
    );
  };

  const cards = [
    { label: 'Cabeças cadastradas', value: dashboard?.totalCabecas ?? 0, icon: 'bi-collection' },
    { label: 'Animais ativos', value: dashboard?.animaisAtivos ?? 0, icon: 'bi-check-circle' },
    { label: 'Nascimentos no mês', value: dashboard?.nascimentosMes ?? 0, icon: 'bi-stars' },
    { label: 'Mortes no mês', value: dashboard?.mortesMes ?? 0, icon: 'bi-heartbreak' },
    { label: 'Leite no mês', value: `${dashboard?.producaoLeiteMes ?? 0} L`, icon: 'bi-droplet' },
    { label: 'Vacinas pendentes', value: dashboard?.vacinasPendentes ?? 0, icon: 'bi-prescription2' },
    { label: 'GMD médio', value: `${dashboard?.gmdMedio ?? 0} kg/dia`, icon: 'bi-graph-up-arrow' },
    { label: 'Resultado pecuário', value: `R$ ${(dashboard?.lucroPecuaria ?? 0).toFixed(2)}`, icon: 'bi-cash-stack' }
  ];

  const columns = [
    { name: 'Brinco', selector: (row) => row.brinco, sortable: true },
    { name: 'Código', selector: (row) => row.codigoInterno, sortable: true },
    { name: 'Nome', selector: (row) => row.nome || '-', sortable: true },
    { name: 'Fazenda', selector: (row) => row.propriedadeNome || '-', sortable: true },
    { name: 'Lote', selector: (row) => row.loteNome || '-', sortable: true },
    { name: 'Categoria', selector: (row) => categoriaOptions.find((item) => item.value === row.categoria)?.label || row.categoria, sortable: true },
    { name: 'Peso atual', selector: (row) => row.pesoAtual ?? row.pesoInicial, sortable: true, right: true },
    { name: 'GMD', selector: (row) => row.gmd ?? 0, sortable: true, right: true },
    { name: 'Status', selector: (row) => statusLabels[row.status] || row.status, sortable: true },
    {
      name: 'Ações de baixa',
      minWidth: '190px',
      width: '190px',
      cell: (row) => row.status === 1 ? (
        <div className="d-flex flex-nowrap gap-2 py-1">
          <button className="btn btn-outline-danger btn-sm text-nowrap" onClick={() => baixarAnimal(row.id, 'morte')}>
            <i className="bi bi-heartbreak me-1" />
            Morte
          </button>
          <button className="btn btn-secondary btn-sm text-nowrap text-white" onClick={() => baixarAnimal(row.id, 'abate')}>
            <i className="bi bi-box-arrow-down me-1" />
            Abate
          </button>
        </div>
      ) : '-',
      ignoreRowClick: true
    }
  ];

  const compraColumns = [
    { name: 'Data', selector: (row) => new Date(row.dataCompra).toLocaleDateString(), sortable: true },
    { name: 'Qtd', selector: (row) => row.quantidade, sortable: true, right: true },
    { name: 'Peso', selector: (row) => row.pesoTotal, sortable: true, right: true },
    { name: 'Valor', selector: (row) => `R$ ${Number(row.valorTotal || 0).toFixed(2)}`, sortable: true, right: true },
    { name: 'Conta pagar', selector: (row) => row.despesaId || '-', sortable: true }
  ];

  const vendaColumns = [
    { name: 'Data', selector: (row) => new Date(row.dataVenda).toLocaleDateString(), sortable: true },
    { name: 'Qtd', selector: (row) => row.quantidade, sortable: true, right: true },
    { name: 'Peso', selector: (row) => row.pesoTotal, sortable: true, right: true },
    { name: 'R$/kg', selector: (row) => Number(row.precoKg || 0).toFixed(2), sortable: true, right: true },
    { name: 'Valor', selector: (row) => `R$ ${Number(row.valorTotal || 0).toFixed(2)}`, sortable: true, right: true },
    { name: 'NF-e', selector: (row) => row.emitirNotaFiscal ? 'Sim' : 'Não', sortable: true },
    { name: 'Conta receber', selector: (row) => row.receitaId || '-', sortable: true },
    {
      name: 'Fiscal',
      cell: (row) => row.emitirNotaFiscal && !row.notaFiscalId ? (
        <button className="btn btn-outline-success btn-sm" onClick={() => emitirNotaVenda(row.id)}>
          <i className="bi bi-receipt me-1" />
          Emitir
        </button>
      ) : (row.notaFiscalId || '-'),
      ignoreRowClick: true
    }
  ];

  return (
    <div className="container mt-4">
      <Header />
          <div className="card shadow-lg border-0 rounded-3 mb-4">
            <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn btn-light btn-sm d-flex align-items-center gap-1"
                  onClick={() => navigate('/menu')}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-left text-success" />
                  <span className="text-success fw-semibold">Voltar</span>
                </button>
                <div>
                  <h5 className="mb-0 fw-semibold">Pecuária e criação animal</h5>
                  <small className="text-white-50">Rebanho, lotes, histórico, sanidade, leite, nutrição e resultado</small>
                </div>
              </div>
              <button className="btn btn-light btn-sm text-success fw-semibold" onClick={carregar} disabled={loading}>
                <i className="bi bi-arrow-clockwise me-1" />
                Atualizar
              </button>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2 mb-3" role="tablist" aria-label="Abas da pecuária">
                {pecuariaTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 ${activeTab === tab.key ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setActiveTab(tab.key)}
                    aria-pressed={activeTab === tab.key}
                  >
                    <i className={`bi ${tab.icon}`} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'visao' && (
                <div className="row g-3">
                  {cards.map((card) => (
                    <div className="col-12 col-sm-6 col-xl-3" key={card.label}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="d-flex justify-content-between align-items-start">
                          <span className="text-muted small">{card.label}</span>
                          <i className={`bi ${card.icon} text-success`} />
                        </div>
                        <div className="fs-4 fw-bold mt-2">{card.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {activeTab === 'cadastros' && (
          <div className="row g-4 mb-4">
            <div className="col-12 col-xl-4">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Novo lote</h6>
                </div>
                <form className="card-body" onSubmit={salvarLote}>
                  <div className="mb-3">
                    <label className="form-label">Fazenda</label>
                    <select
                      className="form-select"
                      value={loteForm.propriedadeId}
                      onChange={(e) => setLoteForm({ ...loteForm, propriedadeId: e.target.value })}
                      required
                    >
                      <option value="">Selecione</option>
                      {propriedades.map((item) => (
                        <option key={item.id} value={item.id}>{item.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Nome do lote</label>
                    <input
                      className="form-control"
                      value={loteForm.nome}
                      onChange={(e) => setLoteForm({ ...loteForm, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Observação</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={loteForm.descricao}
                      onChange={(e) => setLoteForm({ ...loteForm, descricao: e.target.value })}
                    />
                  </div>
                  <button className="btn btn-success w-100" type="submit" disabled={loading}>
                    <i className="bi bi-plus-circle me-1" />
                    Salvar lote
                  </button>
                </form>
              </div>
            </div>

            <div className="col-12 col-xl-8">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Entrada de animal</h6>
                </div>
                <form className="card-body" onSubmit={salvarAnimal}>
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label">Fazenda</label>
                      <select
                        className="form-select"
                        value={animalForm.propriedadeId}
                        onChange={(e) => setAnimalForm({ ...animalForm, propriedadeId: e.target.value, lotePecuariaId: '' })}
                        required
                      >
                        <option value="">Selecione</option>
                        {propriedades.map((item) => (
                          <option key={item.id} value={item.id}>{item.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Lote</label>
                      <select
                        className="form-select"
                        value={animalForm.lotePecuariaId}
                        onChange={(e) => setAnimalForm({ ...animalForm, lotePecuariaId: e.target.value })}
                        required
                      >
                        <option value="">Selecione</option>
                        {lotesDisponiveis.map((item) => (
                          <option key={item.id} value={item.id}>{item.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Brinco</label>
                      <input className="form-control" value={animalForm.brinco} onChange={(e) => setAnimalForm({ ...animalForm, brinco: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Código interno</label>
                      <input className="form-control" value={animalForm.codigoInterno} onChange={(e) => setAnimalForm({ ...animalForm, codigoInterno: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Nome</label>
                      <input className="form-control" value={animalForm.nome} onChange={(e) => setAnimalForm({ ...animalForm, nome: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Raca</label>
                      <input className="form-control" value={animalForm.raca} onChange={(e) => setAnimalForm({ ...animalForm, raca: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Sexo</label>
                      <select className="form-select" value={animalForm.sexo} onChange={(e) => setAnimalForm({ ...animalForm, sexo: e.target.value })}>
                        {sexoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Categoria</label>
                      <select className="form-select" value={animalForm.categoria} onChange={(e) => setAnimalForm({ ...animalForm, categoria: e.target.value })}>
                        {categoriaOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Nascimento</label>
                      <input type="date" className="form-control" value={animalForm.dataNascimento} onChange={(e) => setAnimalForm({ ...animalForm, dataNascimento: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Peso inicial</label>
                      <input type="number" step="0.001" className="form-control" value={animalForm.pesoInicial} onChange={(e) => setAnimalForm({ ...animalForm, pesoInicial: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label">Valor compra</label>
                      <CurrencyInput className="form-control" value={animalForm.valorCompra} onValueChange={(value) => setAnimalForm({ ...animalForm, valorCompra: value })} placeholder="0,00" />
                    </div>
                    <div className="col-12 col-md-9">
                      <label className="form-label">Observação</label>
                      <input className="form-control" value={animalForm.observacao} onChange={(e) => setAnimalForm({ ...animalForm, observacao: e.target.value })} />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted small">{propriedadeSelecionada ? `Fazenda selecionada: ${propriedadeSelecionada.nome}` : 'Informe fazenda e lote para gravar o histórico corretamente.'}</span>
                    <button className="btn btn-success" type="submit" disabled={loading}>
                      <i className="bi bi-save me-1" />
                      Salvar animal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'movimentacoes' && (
          <div className="row g-4 mb-4">
            <div className="col-12 col-xl-6">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Compra de animais</h6>
                </div>
                <form className="card-body" onSubmit={registrarCompra}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Fazenda</label>
                      <select className="form-select" value={compraForm.propriedadeId} onChange={(e) => setCompraForm({ ...compraForm, propriedadeId: e.target.value, lotePecuariaId: '' })} required>
                        <option value="">Selecione</option>
                        {propriedades.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Lote de entrada</label>
                      <select className="form-select" value={compraForm.lotePecuariaId} onChange={(e) => setCompraForm({ ...compraForm, lotePecuariaId: e.target.value })} required>
                        <option value="">Selecione</option>
                        {lotesCompraDisponiveis.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Fornecedor</label>
                      <select className="form-select" value={compraForm.fornecedorId} onChange={(e) => setCompraForm({ ...compraForm, fornecedorId: e.target.value })}>
                        <option value="">Sem fornecedor</option>
                        {fornecedores.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Compra</label>
                      <input type="date" className="form-control" value={compraForm.dataCompra} onChange={(e) => setCompraForm({ ...compraForm, dataCompra: e.target.value })} required />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Vencimento</label>
                      <input type="date" className="form-control" value={compraForm.dataVencimento} onChange={(e) => setCompraForm({ ...compraForm, dataVencimento: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Brinco</label>
                      <input className="form-control" value={compraForm.brinco} onChange={(e) => setCompraForm({ ...compraForm, brinco: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Código</label>
                      <input className="form-control" value={compraForm.codigoInterno} onChange={(e) => setCompraForm({ ...compraForm, codigoInterno: e.target.value })} required />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">Raca</label>
                      <input className="form-control" value={compraForm.raca} onChange={(e) => setCompraForm({ ...compraForm, raca: e.target.value })} required />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Sexo</label>
                      <select className="form-select" value={compraForm.sexo} onChange={(e) => setCompraForm({ ...compraForm, sexo: e.target.value })}>
                        {sexoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Categoria</label>
                      <select className="form-select" value={compraForm.categoria} onChange={(e) => setCompraForm({ ...compraForm, categoria: e.target.value })}>
                        {categoriaOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Peso</label>
                      <input type="number" step="0.001" className="form-control" value={compraForm.pesoEntrada} onChange={(e) => setCompraForm({ ...compraForm, pesoEntrada: e.target.value })} required />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Valor</label>
                      <CurrencyInput className="form-control" value={compraForm.valor} onValueChange={(value) => setCompraForm({ ...compraForm, valor: value })} placeholder="0,00" required />
                    </div>
                  </div>
                  <button className="btn btn-success mt-3 w-100" type="submit" disabled={loading}>
                    <i className="bi bi-cart-plus me-1" />
                    Registrar compra
                  </button>
                </form>
              </div>
            </div>

            <div className="col-12 col-xl-6">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Venda de animais</h6>
                </div>
                <form className="card-body" onSubmit={registrarVenda}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Fazenda</label>
                      <select className="form-select" value={vendaForm.propriedadeId} onChange={(e) => { setVendaForm({ ...vendaForm, propriedadeId: e.target.value }); setAnimaisVenda([]); }} required>
                        <option value="">Selecione</option>
                        {propriedades.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Cliente</label>
                      <select className="form-select" value={vendaForm.clienteId} onChange={(e) => setVendaForm({ ...vendaForm, clienteId: e.target.value })} required>
                        <option value="">Selecione</option>
                        {clientes.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Venda</label>
                      <input type="date" className="form-control" value={vendaForm.dataVenda} onChange={(e) => setVendaForm({ ...vendaForm, dataVenda: e.target.value })} required />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Recebimento</label>
                      <input type="date" className="form-control" value={vendaForm.dataRecebimento} onChange={(e) => setVendaForm({ ...vendaForm, dataRecebimento: e.target.value })} required />
                    </div>
                    <div className="col-6 col-md-3">
                      <label className="form-label">Preco/kg</label>
                      <CurrencyInput className="form-control" value={vendaForm.precoKg} onValueChange={(value) => setVendaForm({ ...vendaForm, precoKg: value })} placeholder="0,00" required />
                    </div>
                    <div className="col-6 col-md-3 d-flex align-items-end">
                      <div className="form-check form-switch mb-2">
                        <input className="form-check-input" type="checkbox" checked={vendaForm.emitirNotaFiscal} onChange={(e) => setVendaForm({ ...vendaForm, emitirNotaFiscal: e.target.checked })} />
                        <label className="form-check-label">NF-e</label>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-3 p-2 mt-3" style={{ maxHeight: '190px', overflowY: 'auto' }}>
                    {animaisAtivosVenda.length === 0 && <div className="text-muted small">Nenhum animal ativo para a fazenda selecionada.</div>}
                    {animaisAtivosVenda.map((animal) => (
                      <label className="d-flex align-items-center gap-2 py-1 small" key={animal.id}>
                        <input
                          type="checkbox"
                          checked={animaisVenda.includes(animal.id)}
                          onChange={(e) => {
                            setAnimaisVenda((atual) => e.target.checked
                              ? [...atual, animal.id]
                              : atual.filter((id) => id !== animal.id));
                          }}
                        />
                        <span className="fw-semibold">{animal.brinco}</span>
                        <span>{animal.nome || animal.codigoInterno}</span>
                        <span className="ms-auto">{animal.pesoAtual ?? animal.pesoInicial} kg</span>
                      </label>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted small">
                      {resumoVenda.quantidade} animal(is), {resumoVenda.peso.toFixed(3)} kg, R$ {resumoVenda.valor.toFixed(2)}
                    </span>
                    <button className="btn btn-success" type="submit" disabled={loading || animaisVenda.length === 0}>
                      <i className="bi bi-cart-check me-1" />
                      Registrar venda
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'rotinas' && (
          <div className="card shadow-lg border-0 rounded-3 mb-4">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0 fw-semibold">Rotinas do animal</h6>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-12 col-lg-5">
                  <label className="form-label">Animal</label>
                  <select
                    className="form-select"
                    value={rotinaForm.animalId}
                    onChange={(e) => {
                      const animal = animais.find((item) => String(item.id) === String(e.target.value));
                      setRotinaForm({
                        ...rotinaForm,
                        animalId: e.target.value,
                        propriedadeNascimentoId: animal?.propriedadeId || '',
                        loteNascimentoId: animal?.lotePecuariaId || '',
                        maeId: e.target.value,
                        matrizId: e.target.value
                      });
                    }}
                  >
                    <option value="">Selecione o animal</option>
                    {animais.map((animal) => (
                      <option key={animal.id} value={animal.id}>
                        {animal.brinco} - {animal.nome || animal.codigoInterno} ({statusLabels[animal.status] || animal.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-lg-7 d-flex align-items-end">
                  <div className="text-muted small">
                    {animalSelecionado
                      ? `${animalSelecionado.loteNome || 'Sem lote'} | ${animalSelecionado.pesoAtual ?? animalSelecionado.pesoInicial} kg | GMD ${animalSelecionado.gmd ?? 0} kg/dia`
                      : 'Selecione um animal para liberar pesagem, leite, sanidade, transferência, baixa e nascimento.'}
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarLeite}>
                    <h6 className="fw-semibold">Produção de leite</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label">Data</label>
                        <input type="date" className="form-control" value={rotinaForm.dataLeite} onChange={(e) => setRotina('dataLeite', e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Turno</label>
                        <select className="form-select" value={rotinaForm.turno} onChange={(e) => setRotina('turno', e.target.value)}>
                          {turnoLeiteOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Litros</label>
                        <input type="number" step="0.001" className="form-control" value={rotinaForm.litros} onChange={(e) => setRotina('litros', e.target.value)} required />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={!rotinaForm.animalId || loading}>
                      <i className="bi bi-droplet me-1" />
                      Salvar leite
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarPesagem}>
                    <h6 className="fw-semibold">Pesagem</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label">Data</label>
                        <input type="date" className="form-control" value={rotinaForm.dataPesagem} onChange={(e) => setRotina('dataPesagem', e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Peso</label>
                        <input type="number" step="0.001" className="form-control" value={rotinaForm.pesoPesagem} onChange={(e) => setRotina('pesoPesagem', e.target.value)} required />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Observação</label>
                        <input className="form-control" value={rotinaForm.observacao} onChange={(e) => setRotina('observacao', e.target.value)} />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={!rotinaForm.animalId || loading}>
                      <i className="bi bi-speedometer2 me-1" />
                      Salvar pesagem
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarSanidade}>
                    <h6 className="fw-semibold">Sanidade</h6>
                    <div className="row g-2">
                      <div className="col-12">
                        <label className="form-label">Vacina / medicamento</label>
                        <input className="form-control" value={rotinaForm.medicamento} onChange={(e) => setRotina('medicamento', e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Aplicação</label>
                        <input type="date" className="form-control" value={rotinaForm.dataAplicacao} onChange={(e) => setRotina('dataAplicacao', e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label">Próxima dose</label>
                        <input type="date" className="form-control" value={rotinaForm.proximaDose} onChange={(e) => setRotina('proximaDose', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Responsavel</label>
                        <input className="form-control" value={rotinaForm.responsavel} onChange={(e) => setRotina('responsavel', e.target.value)} required />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={!rotinaForm.animalId || loading}>
                      <i className="bi bi-prescription2 me-1" />
                      Salvar sanidade
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarTransferencia}>
                    <h6 className="fw-semibold">Transferência de lote</h6>
                    <div className="row g-2">
                      <div className="col-12">
                        <label className="form-label">Lote destino</label>
                        <select className="form-select" value={rotinaForm.loteDestinoId} onChange={(e) => setRotina('loteDestinoId', e.target.value)} required>
                          <option value="">Selecione</option>
                          {lotesRotinaDisponiveis.map((lote) => <option key={lote.id} value={lote.id}>{lote.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Data</label>
                        <input type="date" className="form-control" value={rotinaForm.dataEvento} onChange={(e) => setRotina('dataEvento', e.target.value)} required />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={!rotinaForm.animalId || loading}>
                      <i className="bi bi-arrow-left-right me-1" />
                      Transferir
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarNutricao}>
                    <h6 className="fw-semibold">Nutrição / custo</h6>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label">Categoria</label>
                        <select className="form-select" value={rotinaForm.categoriaNutricao} onChange={(e) => setRotina('categoriaNutricao', e.target.value)}>
                          {nutricaoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label">Insumo</label>
                        <select className="form-select" value={rotinaForm.insumoId} onChange={(e) => setRotina('insumoId', e.target.value)}>
                          <option value="">Sem insumo</option>
                          {insumos.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-4">
                        <label className="form-label">Qtd</label>
                        <input type="number" step="0.001" className="form-control" value={rotinaForm.quantidadeNutricao} onChange={(e) => setRotina('quantidadeNutricao', e.target.value)} required />
                      </div>
                      <div className="col-4">
                        <label className="form-label">Unidade</label>
                        <input className="form-control" value={rotinaForm.unidadeNutricao} onChange={(e) => setRotina('unidadeNutricao', e.target.value)} required />
                      </div>
                      <div className="col-4">
                        <label className="form-label">Custo</label>
                        <CurrencyInput className="form-control" value={rotinaForm.custoNutricao} onValueChange={(value) => setRotina('custoNutricao', value)} placeholder="0,00" required />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={!rotinaForm.animalId || loading}>
                      <i className="bi bi-box-seam me-1" />
                      Salvar nutrição
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-4">
                  <form className="border rounded-3 p-3 h-100" onSubmit={(e) => registrarBaixaManual(e, 'morte')}>
                    <h6 className="fw-semibold">Morte / abate</h6>
                    <div className="row g-2">
                      <div className="col-4">
                        <label className="form-label">Data</label>
                        <input type="date" className="form-control" value={rotinaForm.dataEvento} onChange={(e) => setRotina('dataEvento', e.target.value)} required />
                      </div>
                      <div className="col-4">
                        <label className="form-label">Peso</label>
                        <input type="number" step="0.001" className="form-control" value={rotinaForm.peso} onChange={(e) => setRotina('peso', e.target.value)} />
                      </div>
                      <div className="col-4">
                        <label className="form-label">Valor</label>
                        <CurrencyInput className="form-control" value={rotinaForm.valor} onValueChange={(value) => setRotina('valor', value)} placeholder="0,00" />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Documento ou observação</label>
                        <input className="form-control" value={rotinaForm.documentoReferencia} onChange={(e) => setRotina('documentoReferencia', e.target.value)} />
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <button className="btn btn-outline-danger btn-sm w-50" disabled={!rotinaForm.animalId || loading}>
                        Morte
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm w-50" disabled={!rotinaForm.animalId || loading} onClick={(e) => registrarBaixaManual(e, 'abate')}>
                        Abate
                      </button>
                    </div>
                  </form>
                </div>

                <div className="col-12 col-xl-6">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarReproducao}>
                    <h6 className="fw-semibold">Controle reprodutivo</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Matriz</label>
                        <select className="form-select" value={rotinaForm.matrizId || rotinaForm.animalId} onChange={(e) => setRotina('matrizId', e.target.value)} required>
                          <option value="">Selecione</option>
                          {animais.map((animal) => <option key={animal.id} value={animal.id}>{animal.brinco} - {animal.nome || animal.codigoInterno}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Reprodutor</label>
                        <select className="form-select" value={rotinaForm.reprodutorId} onChange={(e) => setRotina('reprodutorId', e.target.value)}>
                          <option value="">Não informado</option>
                          {animais.map((animal) => <option key={animal.id} value={animal.id}>{animal.brinco} - {animal.nome || animal.codigoInterno}</option>)}
                        </select>
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Inseminacao</label>
                        <input type="date" className="form-control" value={rotinaForm.dataInseminacao} onChange={(e) => setRotina('dataInseminacao', e.target.value)} />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Cobertura</label>
                        <input type="date" className="form-control" value={rotinaForm.dataCobertura} onChange={(e) => setRotina('dataCobertura', e.target.value)} />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Diagnostico</label>
                        <select className="form-select" value={rotinaForm.diagnostico} onChange={(e) => setRotina('diagnostico', e.target.value)}>
                          {diagnosticoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Parto</label>
                        <input type="date" className="form-control" value={rotinaForm.dataParto} onChange={(e) => setRotina('dataParto', e.target.value)} />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={loading}>
                      <i className="bi bi-calendar-heart me-1" />
                      Salvar reprodução
                    </button>
                  </form>
                </div>

                <div className="col-12 col-xl-6">
                  <form className="border rounded-3 p-3 h-100" onSubmit={registrarNascimento}>
                    <h6 className="fw-semibold">Nascimento</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Fazenda</label>
                        <select className="form-select" value={rotinaForm.propriedadeNascimentoId} onChange={(e) => setRotinaForm({ ...rotinaForm, propriedadeNascimentoId: e.target.value, loteNascimentoId: '' })} required>
                          <option value="">Selecione</option>
                          {propriedades.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Lote</label>
                        <select className="form-select" value={rotinaForm.loteNascimentoId} onChange={(e) => setRotina('loteNascimentoId', e.target.value)} required>
                          <option value="">Selecione</option>
                          {lotesNascimentoDisponiveis.map((lote) => <option key={lote.id} value={lote.id}>{lote.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Brinco</label>
                        <input className="form-control" value={rotinaForm.brincoNascimento} onChange={(e) => setRotina('brincoNascimento', e.target.value)} required />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Código</label>
                        <input className="form-control" value={rotinaForm.codigoNascimento} onChange={(e) => setRotina('codigoNascimento', e.target.value)} required />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Peso</label>
                        <input type="number" step="0.001" className="form-control" value={rotinaForm.pesoNascimento} onChange={(e) => setRotina('pesoNascimento', e.target.value)} required />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Data</label>
                        <input type="date" className="form-control" value={rotinaForm.dataNascimentoNovo} onChange={(e) => setRotina('dataNascimentoNovo', e.target.value)} required />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Sexo</label>
                        <select className="form-select" value={rotinaForm.sexoNascimento} onChange={(e) => setRotina('sexoNascimento', e.target.value)}>
                          {sexoOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label">Categoria</label>
                        <select className="form-select" value={rotinaForm.categoriaNascimento} onChange={(e) => setRotina('categoriaNascimento', e.target.value)}>
                          {categoriaOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Raca</label>
                        <input className="form-control" value={rotinaForm.racaNascimento} onChange={(e) => setRotina('racaNascimento', e.target.value)} required />
                      </div>
                    </div>
                    <button className="btn btn-success btn-sm mt-3 w-100" disabled={loading || !rotinaForm.animalId}>
                      <i className="bi bi-stars me-1" />
                      Registrar nascimento
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'historico' && (
            <div className="card shadow-lg border-0 rounded-3 overflow-hidden">
              <div className="card-header bg-success text-white p-3">
                <h5 className="mb-1"><i className="bi bi-clock-history me-2" />Histórico de rotinas por animal</h5>
                <small className="text-white-50">Consulte toda a vida operacional do animal em uma única linha do tempo.</small>
              </div>
              <div className="card-body p-4">
                <div className="row g-3 mb-4">
                  <div className="col-12 col-lg-5"><label className="form-label fw-semibold">Animal</label><select className="form-select" value={rotinaForm.animalId} onChange={(e)=>setRotina('animalId',e.target.value)}><option value="">Selecione o animal</option>{animais.map(a=><option key={a.id} value={a.id}>{a.brinco} — {a.nome||a.codigoInterno}</option>)}</select></div>
                  <div className="col-12 col-md-4 col-lg-3"><label className="form-label fw-semibold">Tipo de rotina</label><select className="form-select" value={filtroHistorico.tipo} onChange={(e)=>setFiltroHistorico({...filtroHistorico,tipo:e.target.value})}><option value="">Todos os tipos</option>{[...new Set(historicoAnimal.map(x=>x.tipo))].sort().map(tipo=><option key={tipo}>{tipo}</option>)}</select></div>
                  <div className="col-6 col-md-4 col-lg-2"><label className="form-label fw-semibold">Data inicial</label><input type="date" className="form-control" value={filtroHistorico.inicio} onChange={(e)=>setFiltroHistorico({...filtroHistorico,inicio:e.target.value})}/></div>
                  <div className="col-6 col-md-4 col-lg-2"><label className="form-label fw-semibold">Data final</label><input type="date" className="form-control" value={filtroHistorico.fim} onChange={(e)=>setFiltroHistorico({...filtroHistorico,fim:e.target.value})}/></div>
                </div>
                {!rotinaForm.animalId ? <div className="text-center py-5 text-muted"><i className="bi bi-search fs-1 d-block mb-2"/>Selecione um animal para consultar seu histórico.</div> : <DataTable columns={[
                  {id:'dataRotina',name:'Data da rotina',selector:r=>new Date(r.data).getTime(),sortable:true,width:'175px',cell:r=>new Date(r.data).toLocaleString('pt-BR',{dateStyle:'short',timeStyle:'short'})},
                  {name:'Rotina',selector:r=>r.tipo,sortable:true,width:'190px',cell:r=><span className="badge bg-success text-white border border-success px-3 py-2 fw-semibold">{r.tipo}</span>},
                  {name:'Detalhes',selector:r=>r.descricao||'-',wrap:true,grow:2},
                  {name:'Peso',selector:r=>r.peso||0,right:true,width:'110px',cell:r=>r.peso!=null?`${r.peso} kg`:'-'},
                  {name:'Valor',selector:r=>r.valor||0,right:true,width:'140px',cell:r=>r.valor!=null?Number(r.valor).toLocaleString('pt-BR',{style:'currency',currency:'BRL'}):'-'}
                ]} data={historicoFiltrado} defaultSortFieldId="dataRotina" defaultSortAsc={false} pagination paginationPerPage={10} paginationRowsPerPageOptions={[10,20,50]} fixedHeader fixedHeaderScrollHeight="430px" highlightOnHover striped noDataComponent="Nenhum registro encontrado para os filtros informados." />}
              </div>
            </div>
          )}

          {activeTab === 'consultas' && (
          <>
          <div className="card shadow-lg border-0 rounded-3">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0 fw-semibold">Rebanho cadastrado</h6>
            </div>
            <div className="card-body">
              <DataTable
                columns={columns}
                data={animais}
                pagination
                progressPending={loading}
                highlightOnHover
                striped
                responsive
                noDataComponent="Nenhum animal cadastrado."
              />
            </div>
          </div>

          <div className="row g-4 mt-1">
            <div className="col-12 col-xl-6">
              <div className="card shadow-sm border-0 rounded-3">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Compras pecuárias</h6>
                </div>
                <div className="card-body">
                  <DataTable columns={compraColumns} data={compras} pagination dense responsive noDataComponent="Nenhuma compra registrada." />
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-6">
              <div className="card shadow-sm border-0 rounded-3">
                <div className="card-header bg-white">
                  <h6 className="mb-0 fw-semibold">Vendas pecuárias</h6>
                </div>
                <div className="card-body">
                  <DataTable columns={vendaColumns} data={vendas} pagination dense responsive noDataComponent="Nenhuma venda registrada." />
                </div>
              </div>
            </div>
          </div>
          </>
          )}
    </div>
  );
}

export default PecuariaDashboard;
